import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, UserPlus } from 'lucide-react';
import type { Client } from 'shared';
import { api, ApiError } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

/* ==========================================================================
   ClientSearch — Full-screen debounced client search.

   Accessible from the Dashboard search bar tap.
   Results as cards: avatar + nome cognome, telefono, ultimo check-up date.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface ClientSearchResult extends Client {
  ultimoCheckup?: string | null;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ClientSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ClientSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-focus the input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // ------------------------------------------------------------------
  // Debounced search
  // ------------------------------------------------------------------

  const performSearch = useCallback(async (searchQuery: string) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setResults([]);
      setHasSearched(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const data = await api.get<ClientSearchResult[]>(
        `/clients?q=${encodeURIComponent(trimmed)}`,
      );
      setResults(data);
      setHasSearched(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Errore di connessione. Riprova.');
      }
      setResults([]);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!value.trim()) {
        setResults([]);
        setHasSearched(false);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      debounceRef.current = setTimeout(() => {
        performSearch(value);
      }, 300);
    },
    [performSearch],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ------------------------------------------------------------------
  // Format date helper
  // ------------------------------------------------------------------

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Nessun check-up';
    try {
      return new Intl.DateTimeFormat('it-IT', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(dateStr));
    } catch {
      return 'Nessun check-up';
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div className="flex h-dvh flex-col bg-surface-base">
      {/* ---- Fixed search header ---- */}
      <header
        className={cn(
          'sticky top-0 z-[var(--z-sticky)]',
          'flex items-center gap-3',
          'px-5 py-3',
          'bg-surface-base/80 backdrop-blur-lg',
          'border-b border-border-default',
        )}
      >
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className={cn(
            'flex items-center justify-center',
            'h-[var(--touch-min)] w-[var(--touch-min)]',
            '-ml-2 rounded-[var(--radius-md)]',
            'text-text-primary',
            'transition-colors duration-[var(--duration-fast)]',
            'hover:bg-surface-sunken active:bg-border-default',
            'cursor-pointer',
          )}
          aria-label="Indietro"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </button>

        {/* Search input */}
        <div className="relative flex-1">
          <Search
            className={cn(
              'absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5',
              'text-text-muted',
              'pointer-events-none',
            )}
            strokeWidth={1.5}
            aria-hidden
          />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Cerca per nome, cognome o telefono..."
            className={cn(
              'w-full h-12 pl-12 pr-4',
              'bg-surface-card border border-border-default',
              'rounded-[var(--radius-xl)]',
              'text-base text-text-primary',
              'font-[family-name:var(--font-body)]',
              'placeholder:text-text-muted',
              'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
              'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
              'focus:outline-none',
            )}
            autoComplete="off"
          />
        </div>
      </header>

      {/* ---- Scrollable content ---- */}
      <main className="flex-1 overflow-y-auto px-5 py-4 mx-auto w-full max-w-3xl">
        {/* Error state */}
        {error && (
          <div className="fade-in rounded-[var(--radius-lg)] border border-status-negative/20 bg-status-negative-light p-4 mb-4">
            <p className="text-sm text-status-negative font-medium">{error}</p>
          </div>
        )}

        {/* Loading skeleton */}
        {isSearching && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center gap-4 rounded-[var(--radius-lg)] border border-border-default bg-surface-card p-4"
              >
                <Skeleton circle width={48} height={48} />
                <div className="flex-1 space-y-2">
                  <Skeleton height={16} className="w-2/5" />
                  <Skeleton height={13} className="w-1/3" />
                </div>
                <Skeleton height={13} className="w-24" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state — no query */}
        {!isSearching && !hasSearched && !error && (
          <div className="fade-in flex flex-col items-center justify-center py-20 text-center">
            <div
              className={cn(
                'mb-5 flex h-16 w-16 items-center justify-center',
                'rounded-full bg-accent-primary-light',
              )}
            >
              <Search className="h-7 w-7 text-accent-primary" strokeWidth={1.5} />
            </div>
            <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
              Cerca una cliente
            </p>
            <p className="mt-2 text-sm text-text-muted max-w-xs">
              Cerca per nome, cognome o telefono per visualizzare il profilo
            </p>
          </div>
        )}

        {/* No results state */}
        {!isSearching && hasSearched && results.length === 0 && !error && (
          <div className="fade-in flex flex-col items-center justify-center py-20 text-center">
            <div
              className={cn(
                'mb-5 flex h-16 w-16 items-center justify-center',
                'rounded-full bg-surface-sunken',
              )}
            >
              <UserPlus className="h-7 w-7 text-text-muted" strokeWidth={1.5} />
            </div>
            <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
              Nessun risultato per &ldquo;{query.trim()}&rdquo;
            </p>
            <p className="mt-2 text-sm text-text-muted max-w-xs">
              Prova con un altro termine di ricerca o verifica l&apos;ortografia
            </p>
          </div>
        )}

        {/* Results list */}
        {!isSearching && results.length > 0 && (
          <div className="fade-in space-y-3">
            <p className="text-sm text-text-muted mb-2">
              {results.length} {results.length === 1 ? 'risultato' : 'risultati'}
            </p>

            {results.map((client) => (
              <button
                key={client.id}
                onClick={() => navigate(`/clients/${client.id}`)}
                className={cn(
                  'flex w-full items-center gap-4',
                  'rounded-[var(--radius-lg)] border border-border-default bg-surface-card',
                  'p-4 shadow-xs',
                  'text-left cursor-pointer',
                  'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
                  'hover:shadow-sm hover:-translate-y-0.5',
                  'active:translate-y-0 active:shadow-xs',
                  'min-h-[var(--touch-comfortable)]',
                )}
              >
                <Avatar
                  name={`${client.nome} ${client.cognome}`}
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-[family-name:var(--font-heading)] text-base font-semibold text-text-primary truncate">
                    {client.nome} {client.cognome}
                  </p>
                  {client.telefono && (
                    <p className="text-sm text-text-secondary mt-0.5 truncate">
                      {client.telefono}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-text-muted">Ultimo check-up</p>
                  <p className="text-sm text-text-secondary font-medium mt-0.5">
                    {formatDate(client.ultimoCheckup)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Bottom spacer */}
        <div className="h-8" />
      </main>
    </div>
  );
}
